import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import api, { formatDate, formatPrice, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Package as PackageIcon, X, Search, Plus, AlertTriangle, ChevronRight } from "lucide-react";
import PackageUsageDrill from "../components/PackageUsageDrill";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "low", label: "Low" },
  { key: "depleted", label: "Depleted" },
  { key: "expired", label: "Expired" },
];

const STATUS_STYLES = {
  active: "bg-status-success-bg text-status-success",
  low: "bg-status-warning-bg text-status-warning",
  depleted: "bg-slate/15 text-slate",
  expired: "bg-status-danger-bg/50 text-status-danger",
};

export default function Packages() {
  const [tab, setTab] = useState("packages"); // packages | types
  const [packages, setPackages] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [lowBalance, setLowBalance] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showSell, setShowSell] = useState(false);
  const [showCreateType, setShowCreateType] = useState(false);
  const [drillPackageId, setDrillPackageId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    const [{ data: pkgs }, { data: low }, { data: types }, { data: svcs }] = await Promise.all([
      api.get("/packages?status=all"),
      api.get("/packages/low-balance"),
      api.get("/package-types"),
      api.get("/services"),
    ]);
    setPackages(pkgs);
    setLowBalance(low);
    setPackageTypes(types);
    setServices(svcs);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filteredPackages = useMemo(() => {
    let rows = packages;
    if (statusFilter !== "all") rows = rows.filter((p) => p.status === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((p) => {
        const c = p.client || {};
        return (
          (c.first_name || "").toLowerCase().includes(q) ||
          (c.last_initial || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (p.package_type?.name || "").toLowerCase().includes(q)
        );
      });
    }
    return rows;
  }, [packages, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { all: packages.length, active: 0, low: 0, depleted: 0, expired: 0 };
    packages.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [packages]);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Packages</p>
          <h1 className="font-display text-4xl text-charcoal">Package ledger</h1>
          <p className="text-sm font-ui text-slate mt-1">
            {packages.length} package{packages.length === 1 ? "" : "s"} on file · {lowBalance.length} low balance · {packageTypes.length} package type{packageTypes.length === 1 ? "" : "s"} configured
          </p>
        </div>
        <div className="flex gap-2">
          {tab === "packages" ? (
            <button onClick={() => setShowSell(true)} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md flex items-center gap-1.5" data-testid="open-sell-package">
              <Plus className="w-4 h-4" /> Sell package
            </button>
          ) : (
            <button onClick={() => setShowCreateType(true)} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md flex items-center gap-1.5" data-testid="open-create-type">
              <Plus className="w-4 h-4" /> New package type
            </button>
          )}
        </div>
      </div>

      {/* Tab switch */}
      <div className="border-b border-hairline mb-5 flex gap-1">
        {[
          { key: "packages", label: "Sold packages" },
          { key: "types", label: "Package types" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-ui border-b-2 -mb-px transition ${tab === t.key ? "border-forest text-forest" : "border-transparent text-slate hover:text-charcoal"}`}
            data-testid={`packages-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-sm font-ui text-status-danger" data-testid="packages-error">{error}</div>}

      {tab === "packages" && (
        <>
          {lowBalance.length > 0 && (
            <div className="bg-status-warning-bg/60 border border-hairline rounded-lg p-4 mb-4 flex items-center gap-2" data-testid="low-balance-banner">
              <AlertTriangle className="w-4 h-4 text-status-warning" />
              <p className="font-ui text-sm text-status-warning">{lowBalance.length} {lowBalance.length === 1 ? "client has" : "clients have"} 2 or fewer sessions remaining.</p>
            </div>
          )}

          <div className="mb-4 flex flex-wrap items-center gap-2">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`px-3 py-1.5 rounded-pill text-xs font-ui border transition ${statusFilter === s.key ? "bg-forest text-white border-forest" : "border-hairline text-slate hover:bg-cream"}`}
                data-testid={`packages-status-${s.key}`}
              >
                {s.label} <span className="ml-1 font-mono">{counts[s.key] ?? 0}</span>
              </button>
            ))}
            <div className="ml-auto relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client or package…"
                className="pl-9 pr-3 py-1.5 bg-white border border-hairline rounded-md text-sm font-ui w-64"
                data-testid="packages-search"
              />
            </div>
          </div>

          <div className="bg-white border border-hairline rounded-lg shadow-warm overflow-hidden">
            <div className="px-6 py-3 border-b border-hairline grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.18em] text-slate">
              <span className="col-span-3">Client</span>
              <span className="col-span-3">Package</span>
              <span className="col-span-2 text-center">Sessions</span>
              <span className="col-span-1 text-center">Status</span>
              <span className="col-span-2">Purchased</span>
              <span className="col-span-1 text-right">Expires</span>
            </div>
            <ul className="divide-y divide-hairline" data-testid="packages-table">
              {filteredPackages.length === 0 && <li className="px-6 py-12 text-center text-sm font-ui text-slate">No packages match your filters.</li>}
              {filteredPackages.map((p) => {
                const remaining = p.remaining ?? (p.original_quantity - p.used_quantity);
                const ratio = remaining / Math.max(p.original_quantity, 1);
                const dotClass = ratio > 0.5 ? "bg-sage" : ratio > 0.2 ? "bg-status-warning" : "bg-status-danger";
                const statusCls = STATUS_STYLES[p.status] || "bg-slate/15 text-slate";
                const open = drillPackageId === p.id;
                return (
                  <li key={p.id} data-testid={`package-row-${p.id}`}>
                    <button
                      onClick={() => setDrillPackageId(open ? null : p.id)}
                      className={`w-full text-left px-6 py-4 grid grid-cols-12 items-center text-sm font-ui hover:bg-cream/30 transition ${open ? "bg-cream/40" : ""}`}
                      title="Click to see this package's usage history"
                      data-testid={`package-row-toggle-${p.id}`}
                    >
                      <div className="col-span-3 flex items-center gap-2">
                        <ChevronRight className={`w-3.5 h-3.5 text-slate transition-transform ${open ? "rotate-90" : ""}`} />
                        {p.client ? (
                          <Link to={`/admin/clients/${p.client.id}`} onClick={(e) => e.stopPropagation()} className="text-charcoal hover:text-forest">
                            {p.client.first_name} {p.client.last_initial}.
                          </Link>
                        ) : <span className="text-slate">—</span>}
                      </div>
                      <div className="col-span-3 text-charcoal">{p.package_type?.name || "—"}</div>
                      <div className="col-span-2 flex items-center gap-2 justify-center">
                        <span className={`w-2.5 h-2.5 rounded-pill ${dotClass}`} />
                        <span className="font-mono text-charcoal">{remaining} / {p.original_quantity}</span>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className={`px-2 py-0.5 rounded-pill text-[10px] font-mono uppercase tracking-wider ${statusCls}`}>{p.status}</span>
                      </div>
                      <div className="col-span-2 text-slate">{formatDate(p.purchased_at)}</div>
                      <div className="col-span-1 text-right text-slate">{p.expires_at ? formatDate(p.expires_at) : "—"}</div>
                    </button>
                    {open && (
                      <PackageUsageDrill packageId={p.id} onReverse={load} onError={setError} />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      {tab === "types" && (
        <PackageTypesTable
          packageTypes={packageTypes}
          services={services}
          packageCounts={packages.reduce((acc, p) => { acc[p.package_type_id] = (acc[p.package_type_id] || 0) + 1; return acc; }, {})}
        />
      )}

      {showSell && (
        <SellPackageModal
          packageTypes={packageTypes}
          onClose={() => setShowSell(false)}
          onSold={async () => { setShowSell(false); await load(); }}
        />
      )}

      {showCreateType && (
        <CreatePackageTypeModal
          services={services}
          onClose={() => setShowCreateType(false)}
          onCreated={async () => { setShowCreateType(false); await load(); }}
        />
      )}
    </AdminLayout>
  );
}

function PackageTypesTable({ packageTypes, services, packageCounts }) {
  return (
    <div className="bg-white border border-hairline rounded-lg shadow-warm overflow-hidden">
      <div className="px-6 py-3 border-b border-hairline grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.18em] text-slate">
        <span className="col-span-4">Name</span>
        <span className="col-span-2 text-center">Sessions</span>
        <span className="col-span-2 text-right">Price</span>
        <span className="col-span-2 text-center">Expires</span>
        <span className="col-span-2 text-center">Sold</span>
      </div>
      {packageTypes.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No package types yet. Create your first one.</div>}
      <ul className="divide-y divide-hairline" data-testid="package-types-table">
        {packageTypes.map((pt) => {
          const eligibleNames = (pt.eligible_service_ids || []).map((id) => services.find((s) => s.id === id)?.name).filter(Boolean);
          return (
            <li key={pt.id} className="px-6 py-4" data-testid={`package-type-row-${pt.id}`}>
              <div className="grid grid-cols-12 items-center text-sm font-ui">
                <div className="col-span-4">
                  <p className="text-charcoal">{pt.name}</p>
                  {eligibleNames.length > 0 && <p className="text-xs text-slate mt-0.5 truncate">Applies to: {eligibleNames.join(", ")}</p>}
                </div>
                <div className="col-span-2 text-center font-mono text-charcoal">{pt.sessions_included}</div>
                <div className="col-span-2 text-right font-mono text-charcoal">{formatPrice(pt.price)}</div>
                <div className="col-span-2 text-center text-slate">{pt.expiry_days ? `${pt.expiry_days}d` : "—"}</div>
                <div className="col-span-2 text-center font-mono text-charcoal">{packageCounts[pt.id] || 0}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SellPackageModal({ packageTypes, onClose, onSold }) {
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [packageTypeId, setPackageTypeId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("external");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      const url = clientSearch.trim() ? `/clients?q=${encodeURIComponent(clientSearch.trim())}` : "/clients";
      api.get(url).then(({ data }) => setClients(data));
    }, 150);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const sell = async () => {
    if (!selectedClient || !packageTypeId) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/packages/sell", {
        client_id: selectedClient.id,
        package_type_id: packageTypeId,
        payment_method: paymentMethod,
      });
      await onSold();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = packageTypes.find((pt) => pt.id === packageTypeId);

  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" data-testid="sell-package-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="font-display text-xl text-charcoal flex items-center gap-2"><PackageIcon className="w-5 h-5 text-sage" /> Sell package</h2>
          <button onClick={onClose} className="p-1 text-slate hover:text-charcoal" data-testid="close-sell-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Client picker */}
          <div>
            <label className="block text-xs font-ui text-slate mb-1">Client</label>
            {selectedClient ? (
              <div className="px-3 py-2.5 bg-cream border border-hairline rounded-md flex items-center justify-between">
                <span className="text-sm font-ui text-charcoal">{selectedClient.first_name} {selectedClient.last_initial}. <span className="text-slate text-xs">· {selectedClient.email}</span></span>
                <button onClick={() => setSelectedClient(null)} className="text-xs text-slate hover:text-status-danger">change</button>
              </div>
            ) : (
              <>
                <input
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search by name, email, or phone…"
                  className={inputCls}
                  data-testid="sell-package-client-search"
                />
                {clients.length > 0 && (
                  <ul className="mt-2 max-h-48 overflow-y-auto border border-hairline rounded-md divide-y divide-hairline">
                    {clients.slice(0, 8).map((c) => (
                      <li key={c.id}>
                        <button
                          onClick={() => setSelectedClient(c)}
                          className="w-full text-left px-3 py-2 text-sm font-ui hover:bg-cream"
                          data-testid={`sell-package-client-option-${c.id}`}
                        >
                          <span className="text-charcoal">{c.first_name} {c.last_initial}.</span>
                          <span className="text-slate text-xs"> · {c.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Package type</span>
            <select value={packageTypeId} onChange={(e) => setPackageTypeId(e.target.value)} className={inputCls} data-testid="sell-package-type">
              <option value="">Select…</option>
              {packageTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>{pt.name} — {formatPrice(pt.price)} · {pt.sessions_included} sessions</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Payment method</span>
            <div className="flex gap-2">
              {[
                { v: "external", l: "External" },
                { v: "cash", l: "Cash" },
                { v: "card", l: "Card (Square)" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setPaymentMethod(opt.v)}
                  className={`flex-1 px-3 py-2 text-sm font-ui rounded-md border ${paymentMethod === opt.v ? "bg-forest text-white border-forest" : "bg-white text-charcoal border-hairline hover:bg-cream"}`}
                  data-testid={`sell-package-method-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            {paymentMethod === "card" && (
              <p className="text-xs font-ui text-slate mt-2">Card capture via Square is currently mocked. The sale will record as a card payment.</p>
            )}
          </label>

          {selectedType && (
            <div className="bg-cream/40 border border-hairline rounded-md px-3 py-2.5 text-sm font-ui">
              <div className="flex items-center justify-between text-charcoal">
                <span>{selectedType.name}</span>
                <span className="font-mono">{formatPrice(selectedType.price)}</span>
              </div>
              <p className="text-xs text-slate mt-0.5">{selectedType.sessions_included} sessions · expires after {selectedType.expiry_days ?? 365}d</p>
            </div>
          )}

          {error && <div className="px-3 py-2 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-xs font-ui text-status-danger" data-testid="sell-package-error">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream">Cancel</button>
          <button
            onClick={sell}
            disabled={!selectedClient || !packageTypeId || submitting}
            className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md disabled:opacity-40"
            data-testid="sell-package-confirm"
          >
            {submitting ? "Recording…" : "Record sale"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatePackageTypeModal({ services, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [sessions, setSessions] = useState(10);
  const [price, setPrice] = useState(0);
  const [expiryDays, setExpiryDays] = useState(365);
  const [eligibleIds, setEligibleIds] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setEligibleIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const create = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    setBusy(true);
    try {
      await api.post("/package-types", {
        name: name.trim(),
        sessions_included: Number(sessions) || 1,
        eligible_service_ids: eligibleIds,
        price: Number(price) || 0,
        expiry_days: expiryDays ? Number(expiryDays) : null,
      });
      await onCreated();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" data-testid="create-type-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="font-display text-xl text-charcoal">New package type</h2>
          <button onClick={onClose} className="p-1 text-slate hover:text-charcoal" data-testid="close-create-type-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="block text-xs font-ui text-slate mb-1">Name</span>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Emotion Code 5-Pack" data-testid="type-name" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-xs font-ui text-slate mb-1">Sessions</span>
              <input type="number" min="1" className={inputCls} value={sessions} onChange={(e) => setSessions(e.target.value)} data-testid="type-sessions" />
            </label>
            <label className="block">
              <span className="block text-xs font-ui text-slate mb-1">Price ($)</span>
              <input type="number" min="0" step="0.01" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} data-testid="type-price" />
            </label>
            <label className="block">
              <span className="block text-xs font-ui text-slate mb-1">Expiry (days)</span>
              <input type="number" min="0" className={inputCls} value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} data-testid="type-expiry" />
            </label>
          </div>
          <div>
            <span className="block text-xs font-ui text-slate mb-1.5">Eligible services</span>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1">
              {services.filter((s) => !s.is_add_on && !s.walk_in_only).map((s) => (
                <button
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  className={`px-2.5 py-1 rounded-pill text-xs font-ui border ${eligibleIds.includes(s.id) ? "bg-forest text-white border-forest" : "border-hairline text-charcoal hover:bg-cream"}`}
                  data-testid={`type-service-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
          {error && <div className="px-3 py-2 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-xs font-ui text-status-danger">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream">Cancel</button>
          <button onClick={create} disabled={busy} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md disabled:opacity-40" data-testid="create-type-confirm">
            {busy ? "Creating…" : "Create type"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";
